import { observer } from 'mobx-react';
import {
  PROFILER_SEARCH_CATEGORY_FIELDS,
  PROFILER_INDUSTRY_CODES_SEARCH_FIELDS
} from '../../../../utils/constants/fields';
import Radio from '../../../../components/inputs/Radio';
import { useStore } from '../../../../store/store';
import IconButton, {
  ICON_BUTTON_ICONS,
  ICON_BUTTON_TYPES
} from '../../../../components/buttons/IconButton';
import { useEffect, useRef, useCallback, useMemo } from 'react';
import { PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES } from '../../../../utils/constants/searchCategories';
import {
  isProfilerAdvancedCriteriaDisabled,
  isProfilerAdvancedCriteriaSituationDisabled,
  isProfilerAdvancedCriteriaOwnershipDisabled
} from '../../../../utils/constants/variables';

const ProfilerIndustryCodesSearchFormDesktop = observer(
  ({
    fields,
    isSearchDisabled,
    onSearch,
    tabs,
    categoryFields,
    setCategoryFieldValue,
    categoryOptions,
    isAdvancedFieldsToggled,
    toggleAdvancedFields,
    searchTreeToggled,
    toggleSearchTree,
    isTyping,
    searchKey,
    setSearchKey,
    industryCodesFlat,
    industryCodesRaw,
    toggleIndustryCode,
    selectIndustryCode,
    toggleAll,
    untoggleAll,
    clearAll
  }) => {
    const { profilerAccessStore } = useStore();

    const searchTreeToggledRef = useRef(searchTreeToggled);

    useEffect(() => {
      searchTreeToggledRef.current = searchTreeToggled;
    }, [searchTreeToggled]);

    useEffect(() => {
      return () => {
        if (searchTreeToggledRef.current) {
          toggleSearchTree();
        }
      };
    }, []);

    const selectedIndustryCodes = industryCodesFlat.filter((code) => code.selected);

    const highlightMatches = useCallback((name, searchKey) => {
      if (!searchKey.trim()) return name;

      const lowerName = name.toLowerCase();
      const lowerSearch = searchKey.toLowerCase();

      const result = [];
      let lastIndex = 0;
      let index = lowerName.indexOf(lowerSearch);

      while (index !== -1) {
        if (index > lastIndex) {
          result.push(name.substring(lastIndex, index));
        }

        result.push(
          <span key={index} className="highlight">
            {name.substring(index, index + searchKey.length)}
          </span>
        );

        lastIndex = index + searchKey.length;
        index = lowerName.indexOf(lowerSearch, lastIndex);
      }

      if (lastIndex < name.length) {
        result.push(name.substring(lastIndex));
      }

      return result;
    }, []);

    const industryCodesMap = useMemo(() => {
      const map = new Map();
      industryCodesFlat.forEach((code) => map.set(code.value, code));
      return map;
    }, [industryCodesFlat]);

    const hasSelectedChildren = useCallback(
      (node) => {
        const currentNode = industryCodesMap.get(node.value);
        if (!currentNode) return false;

        const directSelected = currentNode.children?.some((child) => {
          const childNode = industryCodesMap.get(child.value);
          return childNode?.selected;
        });
        if (directSelected) return true;

        return (
          currentNode.children?.some((child) => {
            const childNode = industryCodesMap.get(child.value);
            return childNode ? hasSelectedChildren(childNode) : false;
          }) || false
        );
      },
      [industryCodesMap]
    );

    const renderIndustryCode = useCallback(
      (code, level) => {
        const fromFlat = industryCodesFlat.find((_c) => _c.value === code.value);
        const hasParent = !!fromFlat.parent;
        const hasChildren = !!code.children?.length;
        const isToggled = !!fromFlat.toggled;
        const isSelected = !!fromFlat.selected;
        const showInnerSelectionsDot =
          !isToggled &&
          !!fromFlat.children?.length &&
          hasSelectedChildren(fromFlat, industryCodesFlat);

        return (
          <div
            className="industry-code-item-wrap"
            key={code.value}
            style={hasParent ? { marginLeft: '22px' } : {}}>
            <div
              className={`industry-code-item noselect ${isSelected ? 'selected' : ''}`}
              onClick={() => {
                if (hasChildren) {
                  toggleIndustryCode(code);
                } else {
                  selectIndustryCode(code);
                }
              }}>
              {hasChildren && (
                <IconButton
                  icon={
                    isToggled ? ICON_BUTTON_ICONS.CHEVRON_DOWN : ICON_BUTTON_ICONS.CHEVRON_RIGHT
                  }
                  withFill={false}
                  withBorder={false}
                  wrapClassName="industry-code-item-toggle"
                />
              )}
              {!hasChildren && <div style={{ width: '18px', height: '18px', flexShrink: 0 }} />}
              <IconButton
                icon={
                  isSelected
                    ? ICON_BUTTON_ICONS.CHECK_SQUARE_FILL
                    : showInnerSelectionsDot
                      ? ICON_BUTTON_ICONS.CHECK_SQUARE
                      : ICON_BUTTON_ICONS.SQUARE
                }
                type={ICON_BUTTON_TYPES.BLUE}
                withFill={false}
                withBorder={false}
                wrapClassName={`industry-code-item-check ${!isSelected && showInnerSelectionsDot ? 'light-blue' : ''}`}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  event.nativeEvent?.preventDefault();
                  selectIndustryCode(code);
                }}
              />
              <div className="industry-code-item-name">
                {isTyping ? code.name : highlightMatches(code.name, searchKey)}
              </div>
              {showInnerSelectionsDot && <div className="inner-selection-dot" />}
            </div>
            {hasChildren &&
              isToggled &&
              code.children.map((child) => renderIndustryCode(child, level + 1))}
          </div>
        );
      },
      [
        highlightMatches,
        hasSelectedChildren,
        industryCodesFlat,
        selectIndustryCode,
        toggleIndustryCode,
        isTyping,
        searchKey
      ]
    );

    const disableSearchForm =
      profilerAccessStore.isProfilerDemo && profilerAccessStore.demoSearchExceededLimit;

    return (
      <div className="formform desktop">
        <div className="fields-area">
          {tabs}
          <div className="row category-group">
            <Radio
              field={PROFILER_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY}
              value={categoryFields[PROFILER_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY.NAME]}
              setFieldValue={setCategoryFieldValue}
              values={categoryOptions}
            />
          </div>
          <div className="flip-horizontal-fields-area">
            {profilerAccessStore.isProfilerDemo && (
              <div
                className={`profiler-demo-info-message${
                  profilerAccessStore.demoSearchExceededLimit ? ' exceeded' : ''
                }`}>
                <div className="img-wrap">
                  <img src={ICON_BUTTON_ICONS.EXCLAMATAION_CIRCLE} />
                </div>
                <div className="text">
                  {profilerAccessStore.demoSearchExceededLimit ? (
                    <>
                      You&apos;ve used all {profilerAccessStore.demoSearchMaxCount} demo searches -
                      upgrade to full access to continue exploring.
                    </>
                  ) : (
                    <>
                      You have {profilerAccessStore.demoSearchRemainingCount} out of{' '}
                      {profilerAccessStore.demoSearchMaxCount} demo searches remaining - upgrade to
                      full access to unlock unlimited searches.
                    </>
                  )}
                </div>
              </div>
            )}

            <div className="search-key-input-wrapper-industry-codes">
              <div className="search-key-label-industry-codes">
                Industries<font className="red-asterisk">*</font>
              </div>
              <div className="search-key-input-wrap-industry-codes">
                <input
                  className={`${selectedIndustryCodes.length ? 'placeholder-black' : ''} ${searchTreeToggled ? 'force-focused' : ''}`}
                  type="text"
                  value={searchKey}
                  onChange={(e) => setSearchKey(e.target.value)}
                  placeholder={
                    selectedIndustryCodes.length === 1
                      ? selectedIndustryCodes[0].name
                      : selectedIndustryCodes.length > 1
                        ? `${selectedIndustryCodes.length} selected`
                        : 'Select'
                  }
                  disabled={disableSearchForm}
                  onFocus={() => {
                    if (!searchTreeToggled) {
                      toggleSearchTree();
                    }
                  }}
                />
                <IconButton
                  id="profiler-industry-codes-drop-icon"
                  icon={
                    searchTreeToggled
                      ? ICON_BUTTON_ICONS.CHEVRON_UP
                      : ICON_BUTTON_ICONS.CHEVRON_DOWN
                  }
                  withFill={false}
                  withBorder={false}
                  onClick={toggleSearchTree}
                  disabled={disableSearchForm}
                />
                {searchTreeToggled && (
                  <div className="industry-codes-wrapper-outer">
                    <div className="industry-codes-actions-wrapper">
                      <IconButton
                        type={ICON_BUTTON_TYPES.BLUE}
                        withFill={false}
                        withBorder={false}
                        innerText="Toggle All"
                        onClick={toggleAll}
                      />
                      <IconButton
                        type={ICON_BUTTON_TYPES.BLUE}
                        withFill={false}
                        withBorder={false}
                        innerText="Untoggle All"
                        onClick={untoggleAll}
                      />
                      <IconButton
                        type={ICON_BUTTON_TYPES.BLUE}
                        withFill={false}
                        withBorder={false}
                        innerText="Clear All"
                        onClick={clearAll}
                      />
                    </div>
                    <div className="industry-codes-wrapper">
                      {industryCodesRaw.map((code) => renderIndustryCode(code, 1))}
                    </div>
                    <div className="fade-top" />
                    <div className="fade-bottom" />
                  </div>
                )}
              </div>
            </div>

            <div className="row">
              {
                fields[
                  PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.REVENUE_MIN.ID +
                    PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.REVENUE_MAX.ID
                ]
              }
              <div className="col"></div>
              {!isAdvancedFieldsToggled ? (
                <div className="col search-btn-wrap">
                  <button
                    className="btn btn-primary disable-fade-primary"
                    disabled={isSearchDisabled}
                    onClick={onSearch}>
                    Search
                  </button>
                </div>
              ) : (
                <div className="col"></div>
              )}
            </div>
            {isAdvancedFieldsToggled &&
              (isProfilerAdvancedCriteriaDisabled ? (
                <>
                  <div className="row">
                    {
                      fields[PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA][
                        PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.EMPLOYEES_MIN.ID +
                          PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.EMPLOYEES_MAX.ID
                      ]
                    }
                    {
                      fields[PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA][
                        PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.HEADQUARTER.ID
                      ]
                    }
                    <div className="col" />
                  </div>
                </>
              ) : (
                <>
                  <div className="row">
                    {
                      fields[PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA][
                        PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.CAGR_MIN.ID +
                          PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.CAGR_MAX.ID
                      ]
                    }
                    {
                      fields[PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA][
                        PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.CAGR_PERIOD.ID
                      ]
                    }
                    {
                      fields[PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA][
                        PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.EBIT_MARGIN_MIN.ID +
                          PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.EBIT_MARGIN_MAX.ID
                      ]
                    }
                  </div>
                  <div className="row">
                    {
                      fields[PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA][
                        PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.EMPLOYEES_MIN.ID +
                          PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.EMPLOYEES_MAX.ID
                      ]
                    }
                    {!isProfilerAdvancedCriteriaSituationDisabled && (
                      <>
                        {
                          fields[
                            PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                          ][PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.SITUATION.ID]
                        }
                      </>
                    )}
                    {isProfilerAdvancedCriteriaSituationDisabled && <div className="col" />}
                    <div className="col"></div>
                  </div>
                  {!isProfilerAdvancedCriteriaOwnershipDisabled && (
                    <div className="row">
                      {
                        fields[PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA][
                          PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.LISTING_STATUS.ID
                        ]
                      }
                      {
                        fields[PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA][
                          PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.OWNERSHIP_STRUCTURES.ID
                        ]
                      }
                      {
                        fields[PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA][
                          PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.OWNERSHIP_BASIS.ID
                        ]
                      }
                    </div>
                  )}
                  {/* Third row: Geo location fields - Country, City/Zipcode, Distance */}
                  <div className="row">
                    {
                      fields[PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA][
                        PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.HEADQUARTER.ID
                      ]
                    }
                    {
                      fields[PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA][
                        PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.CITY_ZIPCODE.ID
                      ]
                    }
                    {
                      fields[PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA][
                        PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.DISTANCE.ID
                      ]
                    }
                  </div>
                </>
              ))}
            {isAdvancedFieldsToggled ? (
              <div className="row">
                <div className="col advanced-criteria-btn-wrap toggled">
                  <IconButton
                    innerText="Main search criteria"
                    icon={ICON_BUTTON_ICONS.CHEVRON_UP}
                    iconOnRight
                    onClick={toggleAdvancedFields}
                    withFill={false}
                    withBorder={false}
                  />
                </div>
                <div className="col"></div>
                <div className="col search-btn-wrap">
                  <button
                    className="btn btn-primary disable-fade-primary"
                    disabled={isSearchDisabled}
                    onClick={onSearch}>
                    Search
                  </button>
                </div>
              </div>
            ) : (
              <div className="row">
                <div className="col advanced-criteria-btn-wrap">
                  <IconButton
                    innerText="Advanced search criteria"
                    icon={ICON_BUTTON_ICONS.CHEVRON_DOWN}
                    iconOnRight
                    onClick={toggleAdvancedFields}
                    withFill={false}
                    withBorder={false}
                  />
                </div>
                <div className="col"></div>
                <div className="col"></div>
              </div>
            )}
            <div
              className="fields-area-mandatory-row"
              style={{ marginTop: isAdvancedFieldsToggled ? '32px' : '94px' }}>
              <font className="red-asterisk">*</font>mandatory fields
            </div>
          </div>
        </div>
      </div>
    );
  }
);

export default ProfilerIndustryCodesSearchFormDesktop;
